/**
 * AugmentedRealityButton.jsx
 * Enterprise-Grade WebXR AR Entry Point
 * Production-ready: capability detection, session lifecycle, error boundaries,
 * permission handling, retry logic, and accessibility.
 *
 * --- UPDATE (Digital Twin MOW+ v1) -------------------------------------------------------
 *   • The AR session now carries the twin's context: START_AR_SESSION's detail includes
 *     `context` ({ selectedAsset, assets, pipelines, sensors, readings, origin }) so the AR
 *     renderer draws the same network and live colours as the 3D view (pass `context`).
 *   • Unsupported devices get a visible one-line reason under the button (tooltips do not
 *     show on touch screens), plus an `ar_session` event for audit (`onAudit`).
 * --- END UPDATE ---------------------------------------------------------------------------
 */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  memo,
} from 'react';
import {
  Button,
  Icon,
  useToast,
  Tooltip,
  Box,
  Text,
  Flex,
  Badge,
  Spinner,
} from '@chakra-ui/react';
import { FaCameraRetro } from 'react-icons/fa';
import { MdSignalWifiStatusbarConnectedNoInternet4 } from 'react-icons/md';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const AR_SESSION_MODE = 'immersive-ar';
const AR_SESSION_FEATURES_REQUIRED = ['local-floor'];
const AR_SESSION_FEATURES_OPTIONAL = ['hit-test', 'dom-overlay', 'anchors', 'depth-sensing'];
const AR_DOM_OVERLAY_ROOT_ID = 'ar-dom-overlay-root';

const CHECK_STATES = Object.freeze({
  IDLE:        'idle',        // Not yet checked
  CHECKING:    'checking',    // Actively checking support
  SUPPORTED:   'supported',   // XR + immersive-ar confirmed
  UNSUPPORTED: 'unsupported', // XR missing or mode unsupported
  UNAVAILABLE: 'unavailable', // Browser has XR but permissions denied / hardware missing
});

const SESSION_STATES = Object.freeze({
  INACTIVE:      'inactive',
  INITIALIZING:  'initializing',
  ACTIVE:        'active',
  ENDING:        'ending',
  ERROR:         'error',
});

// Human-readable AR error codes → user messages
const AR_ERROR_MESSAGES = {
  NotSupportedError:         'WebXR AR is not supported on this browser or device.',
  SecurityError:             'AR session was blocked. Ensure the page is served over HTTPS.',
  NotAllowedError:           'Camera permission was denied. Please allow camera access and try again.',
  InvalidStateError:         'An AR session is already running. Please end it before starting a new one.',
  OperationError:            'AR hardware initialisation failed. Try restarting your browser.',
  AbortError:                'AR session was aborted before it could start.',
  default:                   'An unexpected error occurred while starting the AR session.',
};

// ─────────────────────────────────────────────────────────────────────────────
// UTILITY: Resolve a WebXR DOMException to a human-readable string
// ─────────────────────────────────────────────────────────────────────────────
function resolveArErrorMessage(err) {
  if (!err) return AR_ERROR_MESSAGES.default;
  return AR_ERROR_MESSAGES[err.name] ?? AR_ERROR_MESSAGES.default;
}

// ─────────────────────────────────────────────────────────────────────────────
// UTILITY: Ensure a DOM overlay root element exists for dom-overlay feature
// ─────────────────────────────────────────────────────────────────────────────
function ensureDomOverlayRoot() {
  let root = document.getElementById(AR_DOM_OVERLAY_ROOT_ID);
  if (!root) {
    root = document.createElement('div');
    root.id = AR_DOM_OVERLAY_ROOT_ID;
    root.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999;';
    document.body.appendChild(root);
  }
  return root;
}

// ─────────────────────────────────────────────────────────────────────────────
// HOOK: useArCapability
// Detects WebXR support once on mount. Cleans up properly.
// ─────────────────────────────────────────────────────────────────────────────
function useArCapability() {
  const [checkState, setCheckState] = useState(CHECK_STATES.IDLE);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    // Abort immediately if WebXR API is absent
    if (!('xr' in navigator) || !navigator.xr) {
      setCheckState(CHECK_STATES.UNSUPPORTED);
      return;
    }

    setCheckState(CHECK_STATES.CHECKING);

    let cancelled = false;

    navigator.xr
      .isSessionSupported(AR_SESSION_MODE)
      .then((supported) => {
        if (cancelled || !mountedRef.current) return;
        setCheckState(supported ? CHECK_STATES.SUPPORTED : CHECK_STATES.UNSUPPORTED);
      })
      .catch(() => {
        if (cancelled || !mountedRef.current) return;
        // XR API exists but threw — treat as unavailable (e.g., permissions policy)
        setCheckState(CHECK_STATES.UNAVAILABLE);
      });

    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
  }, []);

  return checkState;
}

// ─────────────────────────────────────────────────────────────────────────────
// HOOK: useArSession
// Manages the full lifecycle of a WebXR AR session.
// ─────────────────────────────────────────────────────────────────────────────
function useArSession({ onSessionStart, onSessionEnd, onError, contextRef = null }) { // --- UPDATE: + contextRef
  const [sessionState, setSessionState] = useState(SESSION_STATES.INACTIVE);
  const sessionRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Safety: end any lingering session when the component unmounts
      if (sessionRef.current) {
        sessionRef.current.end().catch(() => {});
        sessionRef.current = null;
      }
    };
  }, []);

  const startSession = useCallback(async () => {
    if (sessionState !== SESSION_STATES.INACTIVE && sessionState !== SESSION_STATES.ERROR) return;

    if (!mountedRef.current) return;
    setSessionState(SESSION_STATES.INITIALIZING);

    try {
      ensureDomOverlayRoot();

      const sessionInit = {
        requiredFeatures: AR_SESSION_FEATURES_REQUIRED,
        optionalFeatures: AR_SESSION_FEATURES_OPTIONAL,
        domOverlay: { root: document.getElementById(AR_DOM_OVERLAY_ROOT_ID) },
      };

      const xrSession = await navigator.xr.requestSession(AR_SESSION_MODE, sessionInit);
      sessionRef.current = xrSession;

      // Attach end listener so state stays in sync if the user exits via browser UI
      xrSession.addEventListener('end', () => {
        sessionRef.current = null;
        if (mountedRef.current) setSessionState(SESSION_STATES.INACTIVE);
        onSessionEnd?.();
      }, { once: true });

      if (!mountedRef.current) {
        // Component unmounted between requestSession and here — clean up
        xrSession.end().catch(() => {});
        return;
      }

      setSessionState(SESSION_STATES.ACTIVE);

      // Broadcast to Three.js hook via the existing custom event contract
      window.dispatchEvent(new CustomEvent('START_AR_SESSION', { detail: { session: xrSession, context: contextRef?.current || null } })); // --- UPDATE: + twin context

      onSessionStart?.(xrSession);

    } catch (err) {
      console.error('[AR] Session start failed:', err);
      sessionRef.current = null;
      if (mountedRef.current) setSessionState(SESSION_STATES.ERROR);
      onError?.(err);
    }
  }, [sessionState, onSessionStart, onSessionEnd, onError, contextRef]); // --- UPDATE: + contextRef

  const endSession = useCallback(async () => {
    if (!sessionRef.current) return;
    setSessionState(SESSION_STATES.ENDING);
    try {
      await sessionRef.current.end();
    } catch (err) {
      // Session may have already ended — safe to ignore
    } finally {
      sessionRef.current = null;
      if (mountedRef.current) setSessionState(SESSION_STATES.INACTIVE);
    }
  }, []);

  return { sessionState, startSession, endSession };
}

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENT: StatusBadge — visual indicator of current AR / support state
// ─────────────────────────────────────────────────────────────────────────────
const StatusBadge = memo(({ checkState, sessionState }) => {
  if (checkState === CHECK_STATES.CHECKING) {
    return (
      <Badge
        display="inline-flex" alignItems="center" gap={1}
        colorScheme="gray" variant="subtle" fontSize="9px" letterSpacing="0.08em"
      >
        <Spinner size="xs" mr={1} /> DETECTING
      </Badge>
    );
  }
  if (checkState === CHECK_STATES.UNSUPPORTED || checkState === CHECK_STATES.UNAVAILABLE) {
    return (
      <Badge colorScheme="red" variant="subtle" fontSize="9px" letterSpacing="0.08em">
        UNSUPPORTED
      </Badge>
    );
  }
  if (sessionState === SESSION_STATES.ACTIVE) {
    return (
      <Badge colorScheme="green" variant="solid" fontSize="9px" letterSpacing="0.08em"
        sx={{ animation: 'arPulse 2s ease-in-out infinite' }}>
        ● LIVE
      </Badge>
    );
  }
  if (sessionState === SESSION_STATES.INITIALIZING) {
    return (
      <Badge colorScheme="orange" variant="subtle" fontSize="9px" letterSpacing="0.08em">
        CALIBRATING
      </Badge>
    );
  }
  if (sessionState === SESSION_STATES.ERROR) {
    return (
      <Badge colorScheme="red" variant="outline" fontSize="9px" letterSpacing="0.08em">
        ERROR — RETRY
      </Badge>
    );
  }
  return (
    <Badge colorScheme="teal" variant="subtle" fontSize="9px" letterSpacing="0.08em">
      READY
    </Badge>
  );
});

StatusBadge.displayName = 'ArStatusBadge';

// ─────────────────────────────────────────────────────────────────────────────
// TOOLTIP LABEL — determined by support and session state
// ─────────────────────────────────────────────────────────────────────────────
function resolveTooltipLabel(checkState, sessionState) {
  if (checkState === CHECK_STATES.CHECKING)    return 'Detecting WebXR capabilities…';
  if (checkState === CHECK_STATES.UNSUPPORTED) return 'AR requires a WebXR-compatible device (Android/ARCore or iOS WebXR viewer)';
  if (checkState === CHECK_STATES.UNAVAILABLE) return 'AR blocked — check browser permissions or use HTTPS';
  if (sessionState === SESSION_STATES.ACTIVE)  return 'Tap to end the AR session';
  if (sessionState === SESSION_STATES.INITIALIZING) return 'Calibrating GPS and depth sensors…';
  if (sessionState === SESSION_STATES.ERROR)   return 'AR session failed — tap to retry';
  return 'Overlay underground pipelines and assets onto the real world';
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT: AugmentedRealityButton
// ─────────────────────────────────────────────────────────────────────────────
const AugmentedRealityButton = memo(({ onSessionStart, onSessionEnd, context = null, onAudit = null } = {}) => { // --- UPDATE: + context, onAudit
  const toast = useToast();
  const checkState = useArCapability();
  // --- UPDATE
  const contextRef = useRef(context);
  contextRef.current = context;
  // --- END UPDATE

  const handleError = useCallback((err) => {
    toast({
      title: 'AR Session Failed',
      description: resolveArErrorMessage(err),
      status: 'error',
      duration: 7000,
      isClosable: true,
      position: 'top-right',
    });
  }, [toast]);

  const handleSessionStart = useCallback((xrSession) => {
    toast({
      title: 'X-Ray Visor Active',
      description: 'Rendering underground pipelines. Aim camera at the ground to see assets.',
      status: 'info',
      duration: 4000,
      isClosable: true,
      position: 'top-right',
    });
    onSessionStart?.(xrSession);
    onAudit?.({ event: 'ar_session_started', at: new Date().toISOString(), asset_id: contextRef.current?.selectedAsset?.id ?? null }); // --- UPDATE
  }, [toast, onSessionStart, onAudit]);

  const handleSessionEnd = useCallback(() => {
    toast({
      title: 'AR Session Ended',
      description: 'Returned to standard view.',
      status: 'success',
      duration: 2500,
      isClosable: true,
      position: 'bottom-right',
    });
    onSessionEnd?.();
    onAudit?.({ event: 'ar_session_ended', at: new Date().toISOString() }); // --- UPDATE
  }, [toast, onSessionEnd, onAudit]);

  const { sessionState, startSession, endSession } = useArSession({
    onSessionStart: handleSessionStart,
    onSessionEnd: handleSessionEnd,
    onError: handleError,
    contextRef, // --- UPDATE
  });

  const isChecking     = checkState === CHECK_STATES.CHECKING;
  const isSupported    = checkState === CHECK_STATES.SUPPORTED;
  const isSessionActive     = sessionState === SESSION_STATES.ACTIVE;
  const isSessionInitializing = sessionState === SESSION_STATES.INITIALIZING;
  const isSessionEnding      = sessionState === SESSION_STATES.ENDING;

  const isDisabled =
    !isSupported ||
    isChecking ||
    isSessionInitializing ||
    isSessionEnding;

  const isLoading = isChecking || isSessionInitializing || isSessionEnding;

  const handleClick = useCallback(() => {
    if (isSessionActive) {
      endSession();
    } else {
      startSession();
    }
  }, [isSessionActive, startSession, endSession]);

  const tooltipLabel = resolveTooltipLabel(checkState, sessionState);

  return (
    <>
      {/* Keyframe injection — one-time, no runtime overhead */}
      <style>{`
        @keyframes arPulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.5; }
        }
        @keyframes arScanLine {
          0%   { background-position: 0 -100%; }
          100% { background-position: 0 200%; }
        }
      `}</style>

      <Tooltip label={tooltipLabel} hasArrow placement="top" openDelay={300}>
        {/*
          Tooltip requires a single focusable child.
          Wrapping Box is needed when the Button may be disabled
          (disabled elements do not fire mouse events, so tooltip won't show).
        */}
        <Box display="inline-flex" w="100%" role="group">
          <Button
            w="100%"
            size="md"
            colorScheme={isSessionActive ? 'red' : 'teal'}
            variant={isSessionActive ? 'solid' : 'outline'}
            leftIcon={
              isLoading
                ? <Spinner size="xs" />
                : <Icon as={isSessionActive ? MdSignalWifiStatusbarConnectedNoInternet4 : FaCameraRetro} />
            }
            onClick={handleClick}
            isDisabled={isDisabled}
            isLoading={false} // We manage the loading icon manually for richer UX
            aria-label={isSessionActive ? 'End AR session' : 'Launch AR X-Ray Visor'}
            aria-busy={isLoading}
            aria-live="polite"
            // Visual polish
            position="relative"
            overflow="hidden"
            fontWeight="700"
            letterSpacing="0.04em"
            borderWidth={isSessionActive ? '0' : '1.5px'}
            borderColor="teal.400"
            boxShadow={
              isSessionActive
                ? '0 0 18px rgba(229, 62, 62, 0.45), inset 0 0 12px rgba(229, 62, 62, 0.15)'
                : '0 0 10px rgba(49, 151, 149, 0.2)'
            }
            _hover={
              isDisabled ? {} : {
                bg: isSessionActive ? 'red.600' : 'teal.50',
                boxShadow: isSessionActive
                  ? '0 0 24px rgba(229, 62, 62, 0.6)'
                  : '0 0 18px rgba(49, 151, 149, 0.45)',
                transform: 'translateY(-1px)',
              }
            }
            _active={{
              transform: 'translateY(0)',
            }}
            _disabled={{
              opacity: 0.48,
              cursor: 'not-allowed',
              boxShadow: 'none',
            }}
            transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
          >
            <Flex direction="column" align="flex-start" gap={0} w="100%">
              <Text fontSize="sm" lineHeight="1.2">
                {isSessionActive     ? 'End AR Session'     :
                 isSessionInitializing ? 'Calibrating…'    :
                 isSessionEnding     ? 'Ending Session…'    :
                 'Launch AR X-Ray Visor'}
              </Text>
              {/* Sub-label for spatial context — production UX detail */}
              {!isSessionActive && !isLoading && isSupported && (
                <Text fontSize="9px" opacity={0.65} fontWeight="400" letterSpacing="0.06em" mt="1px">
                  Underground asset overlay
                </Text>
              )}
            </Flex>

            {/* Status badge — top-right corner of button */}
            <Box position="absolute" top="6px" right="8px">
              <StatusBadge checkState={checkState} sessionState={sessionState} />
            </Box>

            {/* Animated scan-line shimmer when session is active */}
            {isSessionActive && (
              <Box
                position="absolute"
                inset="0"
                pointerEvents="none"
                bgGradient="linear(to-b, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)"
                bgSize="100% 40%"
                sx={{ animation: 'arScanLine 2.4s linear infinite' }}
              />
            )}
          </Button>
        </Box>
      </Tooltip>
      {/* --- UPDATE: visible reason on touch devices */}
      {(checkState === CHECK_STATES.UNSUPPORTED || checkState === CHECK_STATES.UNAVAILABLE) && (
        <Text fontSize="xs" opacity={0.7} mt={1}>{resolveTooltipLabel(checkState, sessionState)}</Text>
      )}
      {/* --- END UPDATE */}
    </>
  );
});

AugmentedRealityButton.displayName = 'AugmentedRealityButton';

export default AugmentedRealityButton;